import type { TakeMeHomeSave } from '@/types/save'
import type { PixelIconName } from '@/game/pixelIcons'
import { LEVELS } from '@/game/levels'
import { BIOME_ORDER, BIOMES, type BiomeId } from '@/styles/theme'

export interface AchievementDef {
  id: string
  name: string
  description: string
  icon: PixelIconName
  earned: (save: TakeMeHomeSave) => boolean
}

function biomeCleared(save: TakeMeHomeSave, biome: BiomeId): boolean {
  const levels = LEVELS.filter((l) => l.biome === biome)
  if (levels.length === 0) return false
  return levels.every((l) => save.progress.completedLevels.includes(l.index))
}

function perfectRoutes(save: TakeMeHomeSave): number {
  return Object.entries(save.progress.levelBest).filter(([k, best]) => {
    const level = LEVELS.find((l) => l.index === Number(k))
    return level ? best.slides <= level.stepTarget : false
  }).length
}

export const ACHIEVEMENTS: AchievementDef[] = [
  {
    id: 'first_home',
    name: 'First Steps Home',
    description: 'Guide Ember home for the first time.',
    icon: 'home',
    earned: (s) => s.progress.completedLevels.length >= 1,
  },
  {
    id: 'ten_levels',
    name: 'Wayfinder',
    description: 'Complete 10 levels.',
    icon: 'levelSelect',
    earned: (s) => s.progress.completedLevels.length >= 10,
  },
  {
    id: 'star_collector',
    name: 'Star Collector',
    description: 'Earn 30 total stars.',
    icon: 'star',
    earned: (s) => s.meta.totalStars >= 30,
  },
  {
    id: 'grace_master',
    name: 'Grace Master',
    description: 'Earn 90 total stars.',
    icon: 'crown',
    earned: (s) => s.meta.totalStars >= 90,
  },
  {
    id: 'route_artist',
    name: 'Route Artist',
    description: 'Clear 10 levels at or under their target slides.',
    icon: 'target',
    earned: (s) => perfectRoutes(s) >= 10,
  },
  {
    id: 'coin_hoarder',
    name: 'Coin Hoarder',
    description: 'Bank 2,000 coins across your journey.',
    icon: 'coins',
    earned: (s) => s.economy.coins >= 2000,
  },
  ...BIOME_ORDER.map<AchievementDef>((biome) => ({
    id: `biome_${biome}`,
    name: `${BIOMES[biome].name} Cleared`,
    description: `Complete every level in ${BIOMES[biome].name}.`,
    icon: 'trophy',
    earned: (s) => biomeCleared(s, biome),
  })),
]

export function evaluateAchievements(save: TakeMeHomeSave): string[] {
  const owned = new Set(save.meta.achievements)
  const newly: string[] = []
  for (const def of ACHIEVEMENTS) {
    if (!owned.has(def.id) && def.earned(save)) newly.push(def.id)
  }
  return newly
}

export function achievementById(id: string): AchievementDef | undefined {
  return ACHIEVEMENTS.find((a) => a.id === id)
}
